import {
  loadAssets,
  loadUsersCsv,
  setAssetsOffset,
  setAssetsSort,
  setAssetsQuery,
  clearSelectedAssets,
  setVehiclesHistoryRowsDescriptor,
  clearAssets,
  refreshAssetsRowsDescriptor,
  selectAsset,
  selectAllAssets,
  loadSelectionScope,
  reloadAssets,
  loadHistoricalVehicles,
  setHistoricalVehiclesOffset,
  setFilterByAssetIds,
  cancelFilterByAssetsImport,
  loadHistoricalNotifications,
  inviteSamsaraUsers,
  inviteSamsaraUser,
  loadFleetData,
  clearImportResponse,
  importAssets,
  checkIfAssetIsDeleted,
  checkIfAssetIsChanged,
  checkIfAssetIsCreated,
  setFilterAssetsSelectOption,
  loadCopilotVersions,
} from './assets.actions';
import {
  updateAssets,
  updateOffset,
  updateQuery,
  updateSort,
  updateVehiclesHistoryRowsDescriptor,
  clearAssetsStore,
  assetStoreSnapshot,
  updateLoadingAssetsStatus,
  loadingSnapshot,
  updateAssetsRowsDescriptor,
  assetStorEntities,
  selectingAllSnapshot,
  selectAllStatus$,
  updateLoadingSelectionScopeStatus,
  updateSelectedAssets,
  updateFleetInformationMap,
  trackAssetsRequests,
  updateHistoricalVehiclesOffset,
  updateHistoricalVehicles,
  updateFilterByAssets,
  updateCancelFilterByAssetsImport,
  updateSmsStatusesMap,
  updateHistoricalNotifications,
  fleetInformationStatus$,
  smsInformationStatus$,
  trackAssetsRequestsStatus,
  updateLinkedDriverMap,
  updateLinkedVehicleMap,
  linkedDataStatus$,
  updateSamsaraInviteStatus,
  updateAssetImportStatus,
  updateImportResponse,
  updateAssetDeleted,
  updateAssetCreatedOrUpdated,
  updateFilterAssetsSelectOption,
  updateCopilotVersions,
} from './assets.repository';
import { accountSummarySnapshot } from '../ams/ams.repository';
import {
  companySettingsSnapshot,
  dataTablePageSizeSnapshot,
  linkedDataEnabled,
  partnerSettingsSnapshot,
} from '../app-settings/app-settings.repository';
import { LicenseUser } from '../../models/license-user';
import { updateToast } from '../toasts.repository';
import { reportToTrackjs } from '../../utils/report-to-trackjs';
import { getErrorContext } from '../../utils/get-error-context';
import { EMPTY_ROWS_DESCRIPTOR } from '../../models/empty-rows-descriptor';
import { downloadFile } from '../../utils/download-file';
import { MAX_PAGE_SIZE, PAGE_SIZE } from '../../models/data-table-settings';
import { HistoricalNotification } from '../../models/historical-notification';
import { mapSmsStatusesToAssets } from '../utils/map-sms-statuses-to-assets';
import { setFilterOptions } from '../../utils/set-filter-options';
import { createLicenseUsers } from '../utils/create-license-users';
import { createInScopeTester } from '../../utils/create-in-scope-tester';
import { LicenseProduct } from '../../models/license-product';
import { ProductInvite } from '../../models/product-invite';
import { isCoPilotLicense } from '../../utils/is-copilot-license';
import { generateCopilotQuickLoginLink } from '../../utils/generate-copilot-quick-login-link';
import { determineMainDriver } from '../../utils/determine-main-driver';
import { createDriverIdLookup } from '../../utils/create-driver-id-lookup';
import { AssetsImportResponse } from '../../models/assets-import-response';
import { chimportAssets } from '../utils/chimport-assets';
import { isFeatureEnabled } from '../feature-flags/feature-flags.repository';
import { lookupAccountCloudProvider } from '../../features/diagnostics/repositories/diagnostics.repository';
import { translate } from '@jsverse/transloco';
import { subscribeOnce } from '@tmx/core';
import {
  AccountsApi,
  CopilotMessagePublisherApi,
  CpihMessage,
  CpihMessageExporterApi,
  FleetApi,
  FleetVehicleInformation,
  NormalizedResponse,
  PagedCollectionParams,
  SmsNotificationServiceApi,
  UserPagedCollectionParams,
} from '@tmx/data-access';
import {
  Subject,
  catchError,
  combineLatest,
  concat,
  filter,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import {
  createEffect,
  dispatch,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import {
  LoadingRowsDescriptor,
  RowDescriptor,
  SelectAllState,
  UpdateRowsDescriptor,
} from '@tmx/operandi';
import { getCurrentAuthenticatedUser } from '@tmx/auth';

const TRACKJS_SOURCE = 'asset effects';
const NO_STATUS_DETAILS_ERROR_CODE = -4007;
const FLEETPORTAL = 'fleetportal';
const SAMSARA = 'samsara';
const TEN_SECONDS_IN_MS = 10000;

const cancelSetSelectionScope$ = new Subject<void>();
const cancelAssetDetails$ = new Subject<void>();

function getAssets(params: UserPagedCollectionParams) {
  cancelAssetDetails$.next();
  const { filterByAssetIds } = assetStoreSnapshot();
  if (filterByAssetIds.length > 0) {
    return AccountsApi.postUsersFleetInfo(params, filterByAssetIds);
  }

  return AccountsApi.getUsersFleetInfo(params);
}

function retrieveLinkedData(deviceIds: string[]) {
  if (partnerSettingsSnapshot().integrationType === 'DriverId') {
    subscribeOnce(
      FleetApi.postDriversVehicleLinks(deviceIds).pipe(
        takeUntil(cancelAssetDetails$),
        trackAssetsRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          updateLinkedVehicleMap(data.driverVehicleLinks);
        })
      )
    );
  }
  if (partnerSettingsSnapshot().integrationType === 'VehicleId') {
    subscribeOnce(
      FleetApi.postVehiclesDriverLinks(deviceIds).pipe(
        takeUntil(cancelAssetDetails$),
        trackAssetsRequestsStatus('linked-data'),
        tapResult(({ data }) => {
          updateLinkedDriverMap(data.vehicleDriverLinkDetails);
        })
      )
    );
  }
}

function retrieveSmsInformation(
  assets: LicenseUser[],
  size: number,
  hasAssets: boolean
) {
  const { accountId } = getCurrentAuthenticatedUser();
  const deviceIds = assets.map((asset) => asset.externalId ?? '');

  subscribeOnce(
    SmsNotificationServiceApi.postSmsNotificationStatusDetails(
      accountId,
      deviceIds
    ).pipe(
      takeUntil(cancelAssetDetails$),
      trackAssetsRequestsStatus('sms-information'),
      catchError((response: NormalizedResponse) => {
        const { error } = response;
        if (
          !Array.isArray(error) &&
          error['errorCode'] === NO_STATUS_DETAILS_ERROR_CODE
        ) {
          return of([]);
        }
        throw error;
      })
    ),
    {
      next: (smsStatuses) => {
        updateSmsStatusesMap(smsStatuses);
        updateAssets(
          mapSmsStatusesToAssets(smsStatuses, assets),
          size,
          hasAssets
        );
        updateLoadingAssetsStatus(false);
      },
      error: (error: NormalizedResponse) => {
        updateToast({
          type: 'danger',
          content: translate('assets.assets.errorLoadingSMSStatusInformation'),
        });

        reportToTrackjs(
          TRACKJS_SOURCE,
          'Error loading sms status information',
          getErrorContext(error)
        );
      },
    }
  );
}

function loadAdditionalAssetData(
  loadLinkedData: boolean,
  loadSmsData: boolean,
  assets: LicenseUser[],
  size: number,
  hasAssets: boolean
) {
  const deviceIds = assets.map((asset) => asset.externalId ?? '');
  if (loadLinkedData) {
    retrieveLinkedData(deviceIds);
  }
  if (loadSmsData) {
    retrieveSmsInformation(assets, size, hasAssets);
  }

  subscribeOnce(
    combineLatest([
      fleetInformationStatus$,
      linkedDataStatus$,
      smsInformationStatus$,
    ]).pipe(
      filter(
        ([fleetStatus, linkedStatus, smsStatus]) =>
          fleetStatus.value !== 'pending' &&
          linkedStatus.value !== 'pending' &&
          smsStatus.value !== 'pending'
      ),
      take(1)
    ),
    () => {
      updateLoadingAssetsStatus(false);
    }
  );
}

const loadAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadAssets),
    tapResult(() => {
      updateLoadingAssetsStatus(true);

      const {
        offset,
        query,
        filterAssetsSelectOption,
        sortDirection,
        sortExpression,
      } = assetStoreSnapshot();

      let forcedSortDirection: '' | 'asc' | 'desc' = 'asc';
      let forcedSortExpression = 'externalId';
      if (sortDirection !== '' && sortExpression !== '') {
        forcedSortDirection = sortDirection;
        forcedSortExpression = sortExpression;
      }

      const params = new UserPagedCollectionParams({
        offset,
        query,
        sortDirection: forcedSortDirection,
        sortExpression: forcedSortExpression,
        limit: dataTablePageSizeSnapshot(),
      });
      setFilterOptions(params, filterAssetsSelectOption);

      subscribeOnce(
        getAssets(params),
        ({ data, unfilteredSize, size }) => {
          const assets = createLicenseUsers(data, accountSummarySnapshot());
          const hasAssets = unfilteredSize > 0;
          updateAssets(assets, size, hasAssets);

          const assetsAsFleetData = data.map((asset) => {
            const fleetData = {
              ...asset,
            } as unknown as FleetVehicleInformation;
            fleetData.deviceId = asset.externalId;
            return fleetData;
          });
          updateFleetInformationMap(assetsAsFleetData);

          const loadLinkedData = linkedDataEnabled() && size > 0;
          const loadSmsData = companySettingsSnapshot().allowSMS && size > 0;

          if (loadLinkedData || loadSmsData) {
            loadAdditionalAssetData(
              loadLinkedData,
              loadSmsData,
              assets,
              size,
              hasAssets
            );
          } else {
            updateLoadingAssetsStatus(false);
          }
        },
        (error) => {
          if (error instanceof Response) {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.errorLoadingAssets'),
            });
          }
          reportToTrackjs(
            TRACKJS_SOURCE,
            'Error loading assets',
            getErrorContext(error)
          );
          updateLoadingAssetsStatus(false);
        }
      );
    })
  )
);

const loadFleetData$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadFleetData),
    tapResult(({ externalIds }) => {
      subscribeOnce(
        FleetApi.postCompanyDevicesInformation(externalIds).pipe(
          takeUntil(cancelAssetDetails$),
          trackAssetsRequestsStatus('fleet-information')
        ),
        (fleetInformation) => {
          updateFleetInformationMap(fleetInformation.data);
        },
        (error) => {
          if (error instanceof Response) {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.errorLoadingFleetInformation'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error loading fleet information',
              getErrorContext(error)
            );
          }
        }
      );
    })
  )
);

const reloadAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(reloadAssets),
    tapResult(() => {
      dispatch(setAssetsOffset({ offset: 0 }));
      dispatch(loadSelectionScope({ reason: 'resize-data' }));
      dispatch(loadAssets());
    })
  )
);

const setAssetsOffset$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsOffset),
    tapResult(({ offset }) => {
      updateOffset(offset);
    })
  )
);

const setAssetsSort$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsSort),
    tapResult(({ sortExpression, sortDirection }) => {
      updateSort(sortExpression, sortDirection);
    })
  )
);

const setAssetsQuery$ = createEffect((actions) =>
  actions.pipe(
    ofType(setAssetsQuery),
    tapResult(({ query }) => {
      updateQuery(query);
    })
  )
);

const setFilterAssetsSelectOption$ = createEffect((actions) =>
  actions.pipe(
    ofType(setFilterAssetsSelectOption),
    tapResult(({ filterAssetsSelectOption }) => {
      updateFilterAssetsSelectOption(filterAssetsSelectOption);
    })
  )
);

const setSelectAsset$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAsset),
    tapResult(({ checked, asset }) => {
      const { selection } = assetStoreSnapshot();
      if (checked) {
        selection.set(asset.externalId ?? '', asset);
      } else {
        selection.delete(asset.externalId ?? '');
      }
      updateSelectedAssets();
      dispatch(refreshAssetsRowsDescriptor());
    })
  )
);

const setSelectAllAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(selectAllAssets),
    tapResult(({ checked }) => {
      dispatch(loadSelectionScope({ reason: 'select-all' }));

      subscribeOnce(
        selectAllStatus$.pipe(filter((status) => status.value === 'success')),
        () => {
          const { selection, selectionScope } = assetStoreSnapshot();

          for (const asset of selectionScope.values()) {
            if (checked) {
              selection.set(asset.externalId ?? '', asset);
            } else {
              selection.delete(asset.externalId ?? '');
            }
          }

          updateSelectedAssets();
          dispatch(refreshAssetsRowsDescriptor());
        }
      );
    })
  )
);

const setAssetsRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(refreshAssetsRowsDescriptor),
    tapResult(() => {
      if (loadingSnapshot()) {
        updateAssetsRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }

      const {
        totalAssets: totalCount,
        selection,
        offset,
        selectionScope,
      } = assetStoreSnapshot();

      if (totalCount === 0) {
        updateAssetsRowsDescriptor(EMPTY_ROWS_DESCRIPTOR);
        return;
      }

      const rows = assetStorEntities().map(
        (asset) =>
          new RowDescriptor(asset, (row) => row.data.id, {
            isSelected: selection.has(asset.externalId ?? ''),
          })
      );

      const inScope = createInScopeTester(selectionScope);
      const totalSelected = Array.from(selection.keys()).reduce(
        (count, id) => count + (inScope(id) ? 1 : 0),
        0
      );

      const selectAllState = selectingAllSnapshot()
        ? SelectAllState.Selecting
        : totalCount === totalSelected
          ? SelectAllState.Selected
          : SelectAllState.Deselected;

      const disabled = selectAllState === SelectAllState.Selecting;

      updateAssetsRowsDescriptor(
        new UpdateRowsDescriptor(
          rows,
          offset,
          totalCount,
          totalSelected,
          selectAllState,
          disabled
        )
      );
    })
  )
);

const clearSelectedAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearSelectedAssets),
    tapResult(() => {
      const { selection } = assetStoreSnapshot();
      selection.clear();
      updateSelectedAssets();
      dispatch(refreshAssetsRowsDescriptor());
    })
  )
);

const setVehiclesHistoryRowsDescriptor$ = createEffect((actions) =>
  actions.pipe(
    ofType(setVehiclesHistoryRowsDescriptor),
    tapResult(({ loading }) => {
      if (loading) {
        updateVehiclesHistoryRowsDescriptor(new LoadingRowsDescriptor());
        return;
      }

      const { historicalVehicles } = assetStoreSnapshot();

      const rows = historicalVehicles.map((vehicle) => {
        return new RowDescriptor(vehicle, (rd) => rd.data.vehicleId);
      });

      updateVehiclesHistoryRowsDescriptor(
        rows.length === 0
          ? EMPTY_ROWS_DESCRIPTOR
          : new UpdateRowsDescriptor(
              rows,
              0,
              rows.length,
              0,
              SelectAllState.Deselected
            )
      );
    })
  )
);

const loadUsersCsv$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadUsersCsv),
    switchMap(() => {
      return AccountsApi.getUsersCsvfile().pipe(
        trackAssetsRequests(['fetch-csv']),
        tapResult(
          (response) => {
            downloadFile(response, 'Assets.csv');
            updateToast({
              type: 'success',
              content: translate('assets.assets.exportedAssets'),
            });
          },
          (error) => {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.failedToExportAssets'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting assets',
              getErrorContext(error)
            );
          }
        )
      );
    })
  )
);

const clearAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearAssets),
    tapResult(() => {
      clearAssetsStore();
    })
  )
);

const setSelectionScope$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadSelectionScope),
    tapResult(({ reason }) => {
      cancelSetSelectionScope$.next();

      updateLoadingSelectionScopeStatus(reason, true);

      if (
        reason === 'select-all' &&
        assetStoreSnapshot().selectionScope.size > 0
      ) {
        updateLoadingSelectionScopeStatus(reason, false);
        return;
      }

      const tempSelectionScope = new Map<string, LicenseUser>();

      const { query, filterAssetsSelectOption, selectionScope } =
        assetStoreSnapshot();

      let params = new UserPagedCollectionParams({
        offset: 0,
        query,
        sortDirection: 'asc',
        sortExpression: 'externalId',
        limit: MAX_PAGE_SIZE,
      });
      setFilterOptions(params, filterAssetsSelectOption);

      subscribeOnce(
        getAssets(params).pipe(takeUntil(cancelSetSelectionScope$)),
        {
          next: ({ data, size, unfilteredSize }) => {
            if (reason === 'resize-data' && size === unfilteredSize) {
              selectionScope.clear();
              updateLoadingSelectionScopeStatus(reason, false);
              return;
            }

            const assets = createLicenseUsers(data, accountSummarySnapshot());
            for (const asset of assets) {
              tempSelectionScope.set(asset.externalId ?? '', asset);
            }

            if (size <= MAX_PAGE_SIZE) {
              selectionScope.clear();
              for (const [id, asset] of tempSelectionScope.entries()) {
                selectionScope.set(id, asset);
              }
              updateLoadingSelectionScopeStatus(reason, false);
              return;
            }

            type RequestType = ReturnType<typeof getAssets>;
            const requests: RequestType[] = [];
            const numRequests = Math.ceil(
              (size - MAX_PAGE_SIZE) / MAX_PAGE_SIZE
            );
            for (let i = 1; i <= numRequests; i += 1) {
              params = new UserPagedCollectionParams({
                offset: i * MAX_PAGE_SIZE,
                query,
                sortDirection: 'asc',
                sortExpression: 'externalId',
                limit: MAX_PAGE_SIZE,
              });
              setFilterOptions(params, filterAssetsSelectOption);

              requests.push(getAssets(params));
            }

            const subscription = concat(...requests)
              .pipe(takeUntil(cancelSetSelectionScope$))
              .subscribe({
                next: ({ data }) => {
                  const assets = createLicenseUsers(
                    data,
                    accountSummarySnapshot()
                  );
                  for (const asset of assets) {
                    tempSelectionScope.set(asset.externalId ?? '', asset);
                  }
                },
                error: () => {
                  selectionScope.clear();
                  updateLoadingSelectionScopeStatus(reason, false);
                  subscription.unsubscribe();
                },
                complete: () => {
                  if (tempSelectionScope.size === size) {
                    selectionScope.clear();
                    for (const [id, asset] of tempSelectionScope.entries()) {
                      selectionScope.set(id, asset);
                    }
                  }
                  updateLoadingSelectionScopeStatus(reason, false);
                  subscription.unsubscribe();
                },
              });
          },
          error: () => {
            selectionScope.clear();
            updateLoadingSelectionScopeStatus(reason, false);
          },
        }
      );
    })
  )
);

const loadHistoricalVehicles$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadHistoricalVehicles),
    switchMap(({ assetId }) => {
      const params = new PagedCollectionParams({
        offset: assetStoreSnapshot().historicalVehiclesOffset,
        limit: PAGE_SIZE,
      });
      return AccountsApi.getUsersVehicleHistory(assetId, params).pipe(
        trackAssetsRequests(['historical-vehicles']),
        tapResult(
          (response) => {
            updateHistoricalVehicles(
              response.vehicles.map((vehicle) => {
                return {
                  ...vehicle,
                  modifiedOn: vehicle.modifiedOn.toString(),
                };
              })
            );
          },
          (error) => {
            updateToast({
              type: 'danger',
              content: translate('assets.assets.failedToExportAssets'),
            });
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting assets',
              getErrorContext(error)
            );
          }
        )
      );
    })
  )
);

const setHistoricalVehiclesOffset$ = createEffect((actions) =>
  actions.pipe(
    ofType(setHistoricalVehiclesOffset),
    tapResult(({ offset }) => {
      updateHistoricalVehiclesOffset(offset);
    })
  )
);

const setFilterByAssetIds$ = createEffect((actions) =>
  actions.pipe(
    ofType(setFilterByAssetIds),
    tapResult(({ filterByAssetIds }) => {
      updateFilterByAssets(filterByAssetIds);
    })
  )
);

const cancelFilterByAssetsImport$ = createEffect((actions) =>
  actions.pipe(
    ofType(cancelFilterByAssetsImport),
    tapResult(() => {
      updateCancelFilterByAssetsImport();
    })
  )
);

const loadHistoricalNotifications$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadHistoricalNotifications),
    switchMap(({ externalId }) => {
      return CopilotMessagePublisherApi.getUsersMessages(externalId).pipe(
        trackAssetsRequestsStatus('load-historical-notifications'),
        tapResult(
          (historicalNotifications) => {
            updateHistoricalNotifications(
              historicalNotifications
                .filter(({ messageType }) => messageType === 'custom_display')
                .map((notification) => new HistoricalNotification(notification))
            );
          },
          () => {
            updateHistoricalNotifications([], true);
          }
        )
      );
    })
  )
);

function createSamsaraInvites(
  asset: LicenseUser,
  driverId: string,
  product: LicenseProduct
) {
  const messages: CpihMessage[] = [];
  if (isCoPilotLicense(product)) {
    messages.push({
      driverId,
      message: translate('assets.assets.samsaraInviteCoPilot', {
        link: generateCopilotQuickLoginLink(asset.externalId ?? ''),
      }),
    });
  } else {
    messages.push({
      driverId,
      message: translate('assets.assets.samsaraInviteMileOn'),
    });
  }
  return messages;
}

function inviteSamsaraAssets(
  inviteRecords: ProductInvite[],
  messages: CpihMessage[]
) {
  subscribeOnce(AccountsApi.postUsersInviteMultiple(inviteRecords), {
    next: () => {
      subscribeOnce(
        CpihMessageExporterApi.postMessageConsumerPartner(
          FLEETPORTAL,
          SAMSARA,
          messages
        ).pipe(trackAssetsRequestsStatus('samsara-invite')),
        {
          complete: () => {
            updateSamsaraInviteStatus();
            updateToast({
              type: 'success',
              content: translate('assets.assets.samsaraInviteSuccess'),
            });
            dispatch(reloadAssets);
          },
          error: (error) => {
            reportToTrackjs(
              TRACKJS_SOURCE,
              'Error exporting assets',
              getErrorContext(error)
            );
            updateToast({
              type: 'danger',
              content: translate('assets.assets.samsaraInviteError'),
            });
          },
        }
      );
    },
  });
}

export const inviteSamsaraUser$ = createEffect((actions) =>
  actions.pipe(
    ofType(inviteSamsaraUser),
    tapResult(({ asset, product }) => {
      const samsaraInvite = {
        id: asset.id ?? 0,
        productId: product.productId ?? 0,
        invitationType: 'Email',
      } as ProductInvite;

      let driverId = '';
      if (partnerSettingsSnapshot().integrationType === 'DriverId') {
        driverId = (
          assetStoreSnapshot().linkedVehicleMap.get(asset.externalId ?? '') ?? {
            integrationDriverId: '',
          }
        ).integrationDriverId;
      } else {
        const mainDriver = determineMainDriver(
          assetStoreSnapshot().linkedDriverMap.get(asset.externalId ?? '') ?? {
            vehicleId: '',
            vehicleDriverLinks: [],
          }
        );
        driverId = mainDriver === null ? '' : mainDriver.integrationDriverId;
      }

      inviteSamsaraAssets(
        [samsaraInvite],
        createSamsaraInvites(asset, driverId, product)
      );
    })
  )
);

export const inviteSamsaraUsers$ = createEffect((actions) =>
  actions.pipe(
    ofType(inviteSamsaraUsers),
    tapResult(({ assets }) => {
      const lookupDriverId = createDriverIdLookup();

      const samsaraInvites: ProductInvite[] = [];
      assets.forEach(({ id, assignments }) => {
        assignments.forEach(({ productId }) => {
          samsaraInvites.push({
            id: id ?? -1,
            productId: productId ?? 0,
            invitationType: 'Email',
          });
        });
      });
      const inviteMap = new Map<string, ProductInvite>(
        samsaraInvites.map((asset) => [`${asset.id}-${asset.productId}`, asset])
      );
      const inviteRecords = Array.from(inviteMap.values());

      let messages: CpihMessage[] = [];

      for (const asset of assets) {
        const driverId = lookupDriverId(asset);

        for (const license of asset.assignments) {
          messages = messages.concat(
            createSamsaraInvites(asset, driverId, license)
          );
        }
      }

      inviteSamsaraAssets(inviteRecords, messages);
    })
  )
);

function completeAssetImport(importResponse: AssetsImportResponse) {
  updateAssetImportStatus();
  updateImportResponse(importResponse);
  if (importResponse.imported > 0) {
    dispatch(reloadAssets());
  }
}

const importAssets$ = createEffect((actions) =>
  actions.pipe(
    ofType(importAssets),
    trackAssetsRequestsStatus('asset-import'),
    tap(() => {
      dispatch(clearImportResponse());
    }),
    chimportAssets(TRACKJS_SOURCE),
    tapResult((importResponse) => {
      const isAzureAccount =
        lookupAccountCloudProvider(
          getCurrentAuthenticatedUser().accountName ?? ''
        ) === 'Azure';
      if (isAzureAccount && isFeatureEnabled('azure-delay')) {
        setTimeout(() => {
          completeAssetImport(importResponse);
        }, 10000);
      } else {
        completeAssetImport(importResponse);
      }
    })
  )
);

const clearImportResponse$ = createEffect((actions) =>
  actions.pipe(
    ofType(clearImportResponse),
    tap(() => {
      updateImportResponse(new AssetsImportResponse());
    })
  )
);

function checkDeletion(time: number, externalId: string) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetDeleted();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      if (response.data.length === 0) {
        updateAssetDeleted();
      } else {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkDeletion(elapsedTime + time, externalId);
      }
    },
  });
}

const checkIfAssetIsDeleted$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsDeleted),
    tapResult(({ externalId }) => {
      checkDeletion(0, externalId);
    })
  )
);

function checkCreation(time: number, externalId: string) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetCreatedOrUpdated();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      if (response.data.length === 0) {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkCreation(elapsedTime + time, externalId);
      } else {
        updateAssetCreatedOrUpdated();
      }
    },
  });
}

const checkIfAssetIsCreated$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsCreated),
    tapResult(({ externalId }) => {
      checkCreation(0, externalId);
    })
  )
);

function checkAssignmentsAreEqual(
  formAssignments: LicenseProduct[],
  responseAssignments: LicenseProduct[]
) {
  for (const assignment of formAssignments) {
    if (
      !responseAssignments.some((res) => res.productId === assignment.productId)
    ) {
      return false;
    }
  }

  return true;
}

function checkAssetChanged(
  time: number,
  externalId: string,
  asset: LicenseUser
) {
  if (time >= TEN_SECONDS_IN_MS) {
    updateAssetCreatedOrUpdated();
    return;
  }
  const startTime = Date.now();

  subscribeOnce(AccountsApi.getUsersFleetInfoAsset(externalId), {
    next: (response) => {
      let updated = false;
      const assets = createLicenseUsers(
        response.data,
        accountSummarySnapshot()
      );
      const newAsset = assets[0];

      if (
        asset.firstName === newAsset.firstName &&
        asset.lastName === newAsset.lastName &&
        asset.email === newAsset.email &&
        asset.phoneNumber === newAsset.phoneNumber &&
        asset.externalId === newAsset.externalId &&
        asset.id === newAsset.id &&
        checkAssignmentsAreEqual(asset.assignments, newAsset.assignments)
      ) {
        updated = true;
      }

      if (updated) {
        updateAssetCreatedOrUpdated();
      } else {
        const endTime = Date.now();
        const elapsedTime = endTime - startTime;
        checkAssetChanged(elapsedTime + time, externalId, asset);
      }
    },
  });
}

const checkIfAssetIsChanged$ = createEffect((actions) =>
  actions.pipe(
    ofType(checkIfAssetIsChanged),
    tapResult(({ externalId, asset }) => {
      checkAssetChanged(0, externalId, asset);
    })
  )
);

const loadCopilotVersions$ = createEffect((actions) =>
  actions.pipe(
    ofType(loadCopilotVersions),
    switchMap(() =>
      AccountsApi.getCompanyAppversionV2().pipe(
        catchError(() => of([])),
        map((values) => values.map((item) => item.appVersion ?? ''))
      )
    ),
    tapResult(updateCopilotVersions)
  )
);

const effects = [
  loadAssets$,
  loadFleetData$,
  reloadAssets$,
  setAssetsOffset$,
  setAssetsSort$,
  setAssetsQuery$,
  setFilterAssetsSelectOption$,
  setSelectAsset$,
  setSelectAllAssets$,
  setAssetsRowsDescriptor$,
  clearSelectedAssets$,
  setVehiclesHistoryRowsDescriptor$,
  loadUsersCsv$,
  clearAssets$,
  setSelectionScope$,
  loadHistoricalVehicles$,
  setHistoricalVehiclesOffset$,
  setFilterByAssetIds$,
  cancelFilterByAssetsImport$,
  loadHistoricalNotifications$,
  inviteSamsaraUser$,
  inviteSamsaraUsers$,
  importAssets$,
  clearImportResponse$,
  checkIfAssetIsDeleted$,
  checkIfAssetIsCreated$,
  checkIfAssetIsChanged$,
  loadCopilotVersions$,
];

export function registerAssetsEffects() {
  registerEffects(effects);
}

export function deregisterAssetsEffects() {
  removeEffects(effects);
}
